import React from 'react';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import { ThemeMode } from './ThemeContext';

/**
 * Colours used to render a small "mini app" preview swatch for a theme option.
 * These mirror the real Pearsana liquid-glass aesthetic: a rainbow / navy canvas
 * gradient with translucent glass surfaces and brand-accent highlights.
 */
export interface ThemePalettePreview {
	/** Canvas gradient — mirrors the app's rainbow (light) / navy (dark) background. */
	background: string;
	/** Brand accent — the active nav pill / highlight colour. */
	accent: string;
	/** Translucent glass sidebar fill. */
	sidebar: string;
	/** Translucent glass card fill. */
	card: string;
	/** Hairline border for glass elements. */
	border: string;
	/** Soft placeholder line colour inside the sidebar. */
	line: string;
}

/**
 * A single selectable theme option.
 *
 * To add a new theme in the future:
 *   1. Add its `id` to `ThemeMode` (and wire it up in `ThemeContext`).
 *   2. Append an entry here with a label, description, icon and preview palette.
 * The selector UI is driven entirely by this catalog, so no UI changes are needed.
 */
export interface ThemeOption {
	id: ThemeMode;
	label: string;
	description: string;
	icon: React.ReactElement;
	preview: ThemePalettePreview;
	/**
	 * Secondary palette for adaptive themes (e.g. "System") that should render a
	 * split light/dark preview swatch.
	 */
	previewAlt?: ThemePalettePreview;
}

const LIGHT_PREVIEW: ThemePalettePreview = {
	// Mirrors .bg-css-rainbow (light) — the real app canvas.
	background: 'linear-gradient(135deg, #e8f4fc 0%, #f0faf5 28%, #fefbe8 52%, #fef0ec 76%, #f3eefa 100%)',
	accent: '#002868',
	sidebar: 'rgba(255, 255, 255, 0.62)',
	card: 'rgba(255, 255, 255, 0.78)',
	border: 'rgba(0, 40, 104, 0.16)',
	line: 'rgba(0, 40, 104, 0.24)'
};

const DARK_PREVIEW: ThemePalettePreview = {
	// Mirrors .bg-css-rainbow[data-theme='dark'] — the real app canvas.
	background: 'linear-gradient(135deg, #000d1f 0%, #001228 28%, #0a0f1a 52%, #120a10 76%, #0a0818 100%)',
	accent: '#5b9bff',
	sidebar: 'rgba(0, 30, 69, 0.82)',
	card: 'rgba(255, 255, 255, 0.10)',
	border: 'rgba(91, 155, 255, 0.24)',
	line: 'rgba(255, 255, 255, 0.30)'
};

export const THEME_CATALOG: ThemeOption[] = [
	{
		id: 'light',
		label: 'Light',
		description: 'Bright and clear',
		icon: <LightModeOutlinedIcon fontSize="small" />,
		preview: LIGHT_PREVIEW
	},
	{
		id: 'system',
		label: 'System',
		description: 'Match your device',
		icon: <SettingsBrightnessOutlinedIcon fontSize="small" />,
		preview: LIGHT_PREVIEW,
		previewAlt: DARK_PREVIEW
	},
	{
		id: 'dark',
		label: 'Dark',
		description: 'Easy on the eyes',
		icon: <DarkModeOutlinedIcon fontSize="small" />,
		preview: DARK_PREVIEW
	}
];
